import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Image as ImageIcon, X, Check, EyeOff, Eye, Upload, Wand2, FileSpreadsheet, Download, Globe, Sliders, Star } from "lucide-react";
import api from "../../lib/api";

// Onglet "Ma Carte" — CRUD des plats du restaurateur. Photos uploadées dans l'Object Storage.
export default function MenuTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = fermé, {} = création, {id,...} = modif
  const [importMode, setImportMode] = useState(null); // null | "csv" | "photo" | "url"
  const [optionsFor, setOptionsFor] = useState(null); // plat pour lequel on gère les options

  const load = async () => {
    try { const { data } = await api.get("/restaurateur/menu"); setItems(data.items || []); }
    catch { toast.error("Impossible de charger la carte."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const askDelete = async (item) => {
    if (!window.confirm(t("restaurateur.menu.confirmDelete"))) return;
    try {
      await api.delete(`/restaurateur/menu/${item.id}`);
      toast.success(t("restaurateur.menu.deleted"));
      setItems((s) => s.filter((x) => x.id !== item.id));
    } catch { toast.error("Erreur lors de la suppression."); }
  };

  const toggleActive = async (item) => {
    try {
      const { data } = await api.patch(`/restaurateur/menu/${item.id}`, { active: !item.active });
      setItems((s) => s.map((x) => (x.id === item.id ? data : x)));
    } catch { toast.error("Erreur."); }
  };

  return (
    <div data-testid="rt-menu-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-head font-black text-2xl text-[#1A1A1A]">
            {t("restaurateur.menu.title")}
            <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${items.length >= 100 ? "bg-red-100 text-red-700" : items.length >= 80 ? "bg-orange-100 text-orange-700" : "bg-[#F0EBE1] text-[#75807A]"}`} data-testid="rt-menu-counter">
              {items.length}/100
            </span>
          </h2>
          <p className="text-sm text-[#75807A] mt-1">
            {t("restaurateur.menu.subtitle")}
            {items.length >= 100 && <span className="ml-1 text-red-600 font-semibold">· Limite atteinte (supprimez un plat pour en ajouter un autre)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setImportMode("url")} data-testid="rt-menu-import-url"
            disabled={items.length >= 100}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#059669] text-[#059669] hover:bg-[#ECFDF5] font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed">
            <Globe className="w-4 h-4" /> Importer depuis mon site
          </button>
          <button onClick={() => setImportMode("photo")} data-testid="rt-menu-import-photo"
            disabled={items.length >= 100}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed">
            <Wand2 className="w-4 h-4" /> {t("restaurateur.menu.importPhoto")}
          </button>
          <button onClick={() => setImportMode("csv")} data-testid="rt-menu-import-csv"
            disabled={items.length >= 100}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] hover:border-[#F26419] font-semibold text-xs text-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed">
            <FileSpreadsheet className="w-4 h-4" /> {t("restaurateur.menu.importCsv")}
          </button>
          <button onClick={() => setEditing({})} data-testid="rt-menu-add"
            disabled={items.length >= 100}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus className="w-4 h-4" /> {t("restaurateur.menu.add")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F26419]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border-2 border-dashed border-[#E2DFD8] p-10 text-center">
          <ImageIcon className="w-10 h-10 text-[#F26419] mx-auto opacity-60" />
          <div className="mt-3 text-sm text-[#75807A]">{t("restaurateur.menu.empty")}</div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((item) => (
            <li key={item.id} className={`rounded-2xl bg-white border border-[#E2DFD8] overflow-hidden ${item.active ? "" : "opacity-60"}`} data-testid={`rt-menu-item-${item.id}`}>
              <div className="aspect-video bg-[#F0EBE1] relative">
                {item.photo_url ? (
                  <img src={item.photo_url + (item.photo_url.includes("?") ? "&" : "?") + "v=" + encodeURIComponent(item.updated_at || "")} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center"><ImageIcon className="w-8 h-8 text-[#B0AA9E]" /></div>
                )}
                <button
                  onClick={() => toggleActive(item)}
                  data-testid={`rt-menu-toggle-${item.id}`}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white/95 shadow text-xs font-semibold text-[#1A1A1A]"
                  title={item.active ? t("restaurateur.menu.active") : t("restaurateur.menu.inactive")}
                >
                  {item.active ? <><Eye className="w-3 h-3 text-[#16a34a]" /> {t("restaurateur.menu.active")}</> : <><EyeOff className="w-3 h-3 text-[#75807A]" /> {t("restaurateur.menu.inactive")}</>}
                </button>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-head font-bold text-[#1A1A1A] truncate">{item.name}</h3>
                    {item.description && <p className="text-xs text-[#75807A] mt-0.5 line-clamp-2">{item.description}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-head font-black text-lg text-[#F26419]">{fmtEuro(item.price_eur)}</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing(item)} data-testid={`rt-menu-edit-${item.id}`} className="flex-1 h-9 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] text-[#1A1A1A] text-xs font-semibold inline-flex items-center justify-center gap-1"><Pencil className="w-3.5 h-3.5" /> {t("restaurateur.menu.edit")}</button>
                  <button onClick={() => setOptionsFor(item)} data-testid={`rt-menu-options-${item.id}`} title="Options de personnalisation" className="h-9 px-3 rounded-full bg-white border border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] text-xs font-semibold inline-flex items-center justify-center gap-1"><Sliders className="w-3.5 h-3.5" /> Options</button>
                  <button onClick={() => askDelete(item)} data-testid={`rt-menu-delete-${item.id}`} className="h-9 px-3 rounded-full bg-white border border-[#D32F2F] text-[#D32F2F] hover:bg-[#FEE2E2] text-xs font-semibold inline-flex items-center justify-center gap-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <MenuItemDialog
          item={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setItems((s) => {
              const idx = s.findIndex((x) => x.id === saved.id);
              if (idx >= 0) { const cp = [...s]; cp[idx] = saved; return cp; }
              return [...s, saved];
            });
            setEditing(null);
          }}
        />
      )}

      {importMode && (
        <ImportDialog
          mode={importMode}
          onClose={() => setImportMode(null)}
          onImported={() => { setImportMode(null); load(); }}
        />
      )}

      {optionsFor && (
        <OptionsDialog item={optionsFor} onClose={() => setOptionsFor(null)} />
      )}
    </div>
  );
}

function fmtEuro(v) {
  try { return new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0); }
  catch { return `${(v || 0).toFixed(2)} €`; }
}

function MenuItemDialog({ item, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [price, setPrice] = useState(item?.price_eur ?? "");
  const [category, setCategory] = useState(item?.category || "");
  const [photoDataUrl, setPhotoDataUrl] = useState(""); // uniquement si nouvelle
  const [photoPreview, setPhotoPreview] = useState(item?.photo_url ? item.photo_url + "?v=" + encodeURIComponent(item?.updated_at || "") : "");
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(t("restaurateur.menu.err.tooBig")); return; }
    const reader = new FileReader();
    reader.onload = () => { setPhotoDataUrl(reader.result); setPhotoPreview(reader.result); setRemovePhoto(false); };
    reader.readAsDataURL(f);
  };

  const onSave = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2) { toast.error(t("restaurateur.menu.err.name")); return; }
    const p = parseFloat(String(price).replace(",", "."));
    if (isNaN(p) || p < 0 || p > 999) { toast.error(t("restaurateur.menu.err.price")); return; }
    setSaving(true);
    try {
      const payload = { name: n, description: description.trim(), price_eur: p, category: category.trim() };
      if (photoDataUrl) payload.photo_data_url = photoDataUrl;
      else if (removePhoto) payload.photo_data_url = "";
      const { data } = isEdit
        ? await api.patch(`/restaurateur/menu/${item.id}`, payload)
        : await api.post("/restaurateur/menu", payload);
      toast.success(t("restaurateur.menu.saved"));
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="rt-menu-dialog">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-black text-xl">{isEdit ? t("restaurateur.menu.edit") : t("restaurateur.menu.add")}</h3>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="rt-menu-dialog-close"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={onSave} className="space-y-4">
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.field.name")} *</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("restaurateur.menu.ph.name")} required data-testid="rt-menu-name"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.field.description")}</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("restaurateur.menu.ph.description")} rows={3} maxLength={500} data-testid="rt-menu-desc"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.field.price")} *</span>
              <input type="number" step="0.01" min="0" max="999" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={t("restaurateur.menu.ph.price")} required data-testid="rt-menu-price"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.field.category")}</span>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60}
                placeholder={t("restaurateur.menu.ph.category")} data-testid="rt-menu-category"
                list="menu-categories"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
              <datalist id="menu-categories">
                <option value="Entrées" /><option value="Plats" /><option value="Pizzas" />
                <option value="Pâtes" /><option value="Desserts" /><option value="Boissons" />
                <option value="Salades" /><option value="Burgers" /><option value="Menus" />
              </datalist>
            </label>
            <div>
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.field.photo")}</span>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-24 h-24 rounded-xl bg-[#F0EBE1] overflow-hidden grid place-items-center shrink-0">
                  {photoPreview && !removePhoto ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-[#B0AA9E]" />}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] text-sm font-semibold cursor-pointer" data-testid="rt-menu-photo-btn">
                    <ImageIcon className="w-4 h-4" /> {photoPreview && !removePhoto ? "Remplacer" : "Choisir une photo"}
                    <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                  </label>
                  {isEdit && item?.photo_url && !photoDataUrl && (
                    <button type="button" onClick={() => { setRemovePhoto(true); setPhotoPreview(""); }} className="text-xs text-[#D32F2F] font-semibold hover:underline" data-testid="rt-menu-photo-remove">
                      {t("restaurateur.menu.removePhoto")}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">{t("common.cancel")}</button>
              <button type="submit" disabled={saving} data-testid="rt-menu-save" className="flex-1 h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? t("restaurateur.menu.saving") : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ImportDialog({ mode, onClose, onImported }) {
  const { t } = useTranslation();
  const [csv, setCsv] = useState("");
  const [replace, setReplace] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [skipMarkup, setSkipMarkup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // liste extraite par l'IA
  const [previewMeta, setPreviewMeta] = useState({ markupApplied: true, ratePct: 25 });
  const [urlValue, setUrlValue] = useState("");
  const [urlPreview, setUrlPreview] = useState(null); // { name, menu_items, menu_count, ... }
  const [urlSelected, setUrlSelected] = useState({}); // { [index]: bool }

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(t("restaurateur.menu.err.tooBig")); return; }
    const r = new FileReader();
    r.onload = () => { setPhotoDataUrl(r.result); setPhotoPreview(r.result); setPreview(null); };
    r.readAsDataURL(f);
  };

  const doCsvImport = async () => {
    if (!csv.trim()) { toast.error(t("restaurateur.menu.import.emptyCsv")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/menu/import-csv", { csv, replace });
      toast.success(t("restaurateur.menu.import.doneCsv", { imported: data.imported, skipped: data.skipped }));
      onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Fichier CSV trop lourd (max 2 Mo)."); e.target.value = ""; return; }
    const r = new FileReader();
    r.onload = () => { setCsv(String(r.result || "")); toast.success(`Fichier "${f.name}" chargé — vérifiez puis cliquez sur Importer.`); };
    r.onerror = () => toast.error("Impossible de lire le fichier.");
    r.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/restaurateur/menu/import-csv/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "nold-go-menu-modele.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const doPhotoAnalyze = async () => {
    if (!photoDataUrl) return;
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/menu/import-photo", { photo_data_url: photoDataUrl, auto_create: false, skip_markup: skipMarkup });
      setPreview(data.items || []);
      setPreviewMeta({ markupApplied: !!data.markup_applied, ratePct: Number(data.commission_rate_pct || 25) });
      toast.success(`${data.count || 0} plats détectés · ${data.markup_applied ? `marge Nold Go +${data.commission_rate_pct}% appliquée` : "prix Uber/Deliveroo conservés"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || t("restaurateur.menu.import.aiFailed")); }
    finally { setBusy(false); }
  };

  const doPhotoConfirm = async () => {
    if (!preview || preview.length === 0) return;
    setBusy(true);
    try {
      // Auto-create maintenant que l'utilisateur a vu le preview
      const { data } = await api.post("/restaurateur/menu/import-photo", { photo_data_url: photoDataUrl, auto_create: true, skip_markup: skipMarkup });
      toast.success(t("restaurateur.menu.import.doneAi", { imported: data.inserted || 0 }));
      onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  const doUrlAnalyze = async () => {
    const u = urlValue.trim();
    if (!u.startsWith("http")) { toast.error("URL invalide (doit commencer par http:// ou https://)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/menu/scrape-website", { url: u });
      const prev = data.preview || {};
      setUrlPreview(prev);
      const items = prev.menu_items || [];
      // Par défaut : tout sélectionné
      const sel = {};
      items.forEach((_, i) => (sel[i] = true));
      setUrlSelected(sel);
      if (items.length === 0) toast.warning("Aucun plat détecté — essayez l'URL directe de votre page menu.");
      else toast.success(`${items.length} plat(s) détecté(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyse échouée."); }
    finally { setBusy(false); }
  };

  const doUrlConfirm = async () => {
    if (!urlPreview) return;
    const chosen = (urlPreview.menu_items || [])
      .map((it, i) => ({ it, i }))
      .filter(({ i }) => urlSelected[i])
      .map(({ it }) => ({
        name: it.name,
        description: it.description || "",
        // Prix majoré de la commission Nold Go 25% (arrondie au centime supérieur côté backend),
        // exactement comme l'import URL admin — préserve la marge du restaurateur.
        price_eur: Number(it.price_with_commission_eur ?? it.price_original_eur ?? it.price_eur ?? 0),
        category: it.category || "",
        image_url: it.image_url || null,
        position: it.position || 0,
      }))
      .filter((it) => it.name && it.price_eur > 0);
    if (chosen.length === 0) { toast.error("Sélectionnez au moins un plat."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/menu/import-from-scrape", { items: chosen, replace });
      toast.success(`${data.inserted} plat(s) importé(s)${data.failed ? ` · ${data.failed} échec(s)` : ""}${data.max_reached ? ` · limite 100 atteinte` : ""}`);
      onImported();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import échoué."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="rt-import-dialog">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-black text-xl">
              {mode === "csv" ? <><FileSpreadsheet className="inline w-5 h-5 mr-2" />{t("restaurateur.menu.importCsv")}</>
                : mode === "url" ? <><Globe className="inline w-5 h-5 mr-2" />Importer depuis mon site</>
                : <><Wand2 className="inline w-5 h-5 mr-2" />{t("restaurateur.menu.importPhoto")}</>}
            </h3>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="rt-import-close"><X className="w-5 h-5" /></button>
          </div>

          {mode === "csv" ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#FAF5EC] p-3 text-xs text-[#3B423E]">
                <b>{t("restaurateur.menu.import.csvHelp")}</b><br />
                Colonnes : <code>nom, prix, description, catégorie</code> (catégorie optionnelle)<br />
                <code className="text-[11px]">Pizza Margherita,12.50,Tomate mozzarella basilic,Pizzas</code><br />
                <code className="text-[11px]">Spaghetti Bolognese;11.50;Sauce maison;Pâtes</code>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={downloadTemplate} data-testid="rt-import-csv-template"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] font-bold text-xs">
                  <Download className="w-4 h-4" /> Télécharger le modèle CSV
                </button>
                <label className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] hover:border-[#F26419] text-[#1A1A1A] font-bold text-xs cursor-pointer" data-testid="rt-import-csv-file-label">
                  <Upload className="w-4 h-4" /> Charger un fichier .csv
                  <input type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" data-testid="rt-import-csv-file" />
                </label>
              </div>
              <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={10} data-testid="rt-import-csv-input"
                placeholder={t("restaurateur.menu.import.csvPlaceholder")}
                className="w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none font-mono text-sm" />
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="rt-import-csv-replace" />
                {t("restaurateur.menu.import.replaceExisting")}
              </label>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">{t("common.cancel")}</button>
                <button onClick={doCsvImport} disabled={busy} data-testid="rt-import-csv-submit"
                  className="flex-1 h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {t("restaurateur.menu.import.import")}
                </button>
              </div>
            </div>
          ) : mode === "url" ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#ECFDF5] border border-[#059669]/25 p-3 text-xs text-[#065F46]">
                <b>Collez l'URL de votre site ou de votre page menu.</b> Nold Go analysera automatiquement les plats, prix, descriptions et photos. Les prix affichés dans l'app seront <b>majorés de la commission Nold Go 25 %</b> (arrondie au centime supérieur) — vous conservez ainsi votre marge nette identique à celle de votre site.
              </div>
              {!urlPreview ? (
                <>
                  <div className="flex gap-2">
                    <input value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="https://mon-restaurant.com/menu" data-testid="rt-import-url-input"
                      className="flex-1 h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#059669] outline-none text-sm" />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">{t("common.cancel")}</button>
                    <button onClick={doUrlAnalyze} disabled={busy || !urlValue.trim()} data-testid="rt-import-url-analyze"
                      className="flex-1 h-12 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                      {busy ? "Analyse en cours…" : "Analyser mon site"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl bg-white border border-[#E2DFD8] p-3">
                    <div className="text-xs text-[#75807A]">Détecté</div>
                    <div className="font-head font-black text-lg text-[#1A1A1A]">{urlPreview.name || "Restaurant"}</div>
                    <div className="text-xs text-[#75807A] mt-0.5">{urlPreview.menu_count || (urlPreview.menu_items || []).length} plat(s) trouvé(s)</div>
                  </div>
                  {(urlPreview.menu_items || []).length === 0 ? (
                    <div className="rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/30 p-3 text-xs text-[#78350F]">
                      Aucun plat détecté sur cette page. Essayez l'URL directe de votre menu (ex. <code>/menu</code>, <code>/carte</code>).
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-xs text-[#75807A]">
                        <span>{Object.values(urlSelected).filter(Boolean).length} / {(urlPreview.menu_items || []).length} sélectionné(s)</span>
                        <div className="flex gap-2">
                          <button onClick={() => { const all = {}; (urlPreview.menu_items || []).forEach((_, i) => (all[i] = true)); setUrlSelected(all); }} data-testid="rt-import-url-select-all" className="text-[#059669] font-semibold hover:underline">Tout cocher</button>
                          <span>·</span>
                          <button onClick={() => setUrlSelected({})} data-testid="rt-import-url-select-none" className="text-[#75807A] font-semibold hover:underline">Tout décocher</button>
                        </div>
                      </div>
                      <ul className="max-h-72 overflow-y-auto rounded-xl border border-[#E2DFD8] divide-y divide-[#E2DFD8]" data-testid="rt-import-url-preview">
                        {(urlPreview.menu_items || []).map((p, i) => (
                          <li key={i} className="p-3 flex items-center gap-3">
                            <input type="checkbox" checked={!!urlSelected[i]} onChange={(e) => setUrlSelected((s) => ({ ...s, [i]: e.target.checked }))} className="w-4 h-4 accent-[#059669]" data-testid={`rt-import-url-item-${i}`} />
                            {p.image_url ? <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="w-12 h-12 rounded-lg bg-[#F0EBE1] shrink-0 grid place-items-center"><ImageIcon className="w-4 h-4 text-[#B0AA9E]" /></div>}
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-[#1A1A1A] truncate">{p.name}</div>
                              {p.description && <div className="text-xs text-[#75807A] truncate">{p.description}</div>}
                              {p.category && <div className="text-[10px] text-[#059669] font-semibold mt-0.5">{p.category}</div>}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-head font-black text-[#059669]">{fmtEuro(p.price_with_commission_eur ?? p.price_original_eur ?? p.price_eur ?? 0)}</div>
                              {p.price_original_eur != null && p.price_with_commission_eur != null && p.price_with_commission_eur !== p.price_original_eur && (
                                <div className="text-[10px] text-[#75807A] line-through">{fmtEuro(p.price_original_eur)}</div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="rt-import-url-replace" />
                        Remplacer tous les plats existants
                      </label>
                    </>
                  )}
                  <div className="flex gap-3">
                    <button onClick={() => { setUrlPreview(null); setUrlSelected({}); }} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">Recommencer</button>
                    <button onClick={doUrlConfirm} disabled={busy || Object.values(urlSelected).filter(Boolean).length === 0} data-testid="rt-import-url-confirm"
                      className="flex-1 h-12 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      {busy ? "Import en cours…" : `Importer ${Object.values(urlSelected).filter(Boolean).length} plat(s)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#FAF5EC] p-3 text-xs text-[#3B423E]">
                <b>{t("restaurateur.menu.import.photoHelp")}</b>
              </div>
              {!preview ? (
                <>
                  <label className="block border-2 border-dashed border-[#E2DFD8] rounded-xl p-8 text-center cursor-pointer hover:border-[#F26419] transition-colors" data-testid="rt-import-photo-drop">
                    {photoPreview ? (
                      <img src={photoPreview} alt="" className="max-h-64 mx-auto rounded-lg" />
                    ) : (
                      <div>
                        <Wand2 className="w-10 h-10 mx-auto text-[#F26419] opacity-70" />
                        <div className="mt-2 text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.import.selectPhoto")}</div>
                        <div className="text-xs text-[#75807A]">{t("restaurateur.menu.import.selectPhotoHint")}</div>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                  </label>
                  <label className="flex items-start gap-2 text-xs text-[#3B423E] cursor-pointer bg-[#FFF3E6] rounded-xl p-3 border border-[#F26419]/30" data-testid="rt-import-photo-skip-markup-label">
                    <input
                      type="checkbox"
                      checked={skipMarkup}
                      onChange={(e) => setSkipMarkup(e.target.checked)}
                      data-testid="rt-import-photo-skip-markup"
                      className="mt-0.5"
                    />
                    <span>
                      <b>Prix déjà majorés</b> (screenshot Uber Eats / Deliveroo)<br />
                      <span className="text-[#75807A]">Cochez si l'image vient d'une plateforme qui applique déjà sa marge, pour ne pas doubler la marge Nold Go.</span>
                    </span>
                  </label>
                  <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">{t("common.cancel")}</button>
                    <button onClick={doPhotoAnalyze} disabled={!photoDataUrl || busy} data-testid="rt-import-photo-analyze"
                      className="flex-1 h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {busy ? t("restaurateur.menu.import.aiWorking") : t("restaurateur.menu.import.analyze")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.menu.import.previewTitle")} ({preview.length})</div>
                  {previewMeta.markupApplied ? (
                    <div className="rounded-xl bg-[#F0FDF4] border border-[#059669]/30 px-3 py-2 text-xs text-[#065F46]" data-testid="rt-import-photo-markup-banner">
                      ✓ Marge Nold Go <b>+{previewMeta.ratePct}%</b> appliquée automatiquement · <span className="text-[#3B423E]">prix original → prix client</span>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/30 px-3 py-2 text-xs text-[#7C2D12]" data-testid="rt-import-photo-markup-banner">
                      ⚠️ Aucune marge ajoutée (Uber Eats / Deliveroo)
                    </div>
                  )}
                  <ul className="max-h-64 overflow-y-auto rounded-xl border border-[#E2DFD8] divide-y divide-[#E2DFD8]" data-testid="rt-import-photo-preview">
                    {preview.map((p, i) => (
                      <li key={i} className="p-3 flex justify-between items-start gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#1A1A1A] truncate">{p.name}</div>
                          {p.description && <div className="text-xs text-[#75807A] truncate">{p.description}</div>}
                        </div>
                        <div className="shrink-0 text-right">
                          {previewMeta.markupApplied && p.price_original_eur != null && Number(p.price_original_eur) !== Number(p.price_eur) && (
                            <div className="text-[11px] text-[#75807A] line-through">{fmtEuro(p.price_original_eur)}</div>
                          )}
                          <div className="font-head font-black text-[#F26419]">{fmtEuro(p.price_eur)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <button onClick={() => setPreview(null)} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">{t("restaurateur.menu.import.retry")}</button>
                    <button onClick={doPhotoConfirm} disabled={busy} data-testid="rt-import-photo-confirm"
                      className="flex-1 h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      {t("restaurateur.menu.import.confirmImport")}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// --------------------------------------------------------------------------
// Options de personnalisation d'un plat (Phase 1 : Dashboard restaurateur uniquement)
// Le frontend client (DishModal / Checkout) ne consomme pas encore ces données —
// c'est l'objet d'une Phase 2 séparée pour ne rien casser en production.
// --------------------------------------------------------------------------
function OptionsDialog({ item, onClose }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/restaurateur/menu/${item.id}/option-groups`);
      setGroups(data.groups || []);
    } catch { toast.error("Impossible de charger les options."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [item.id]);

  const addGroup = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/restaurateur/menu/${item.id}/option-groups`, {
        name: "Nouveau groupe",
        required: false,
        min_choices: 0,
        max_choices: 1,
        position: groups.length,
      });
      setGroups((s) => [...s, { ...data, choices: [] }]);
      toast.success("Groupe créé.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setCreating(false); }
  };

  const updateGroupLocal = (groupId, patch) => {
    setGroups((s) => s.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));
  };

  const removeGroup = async (groupId) => {
    if (!window.confirm("Supprimer ce groupe et tous ses choix ?")) return;
    try {
      await api.delete(`/restaurateur/menu/option-groups/${groupId}`);
      setGroups((s) => s.filter((g) => g.id !== groupId));
      toast.success("Groupe supprimé.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="rt-options-dialog">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <h3 className="font-head font-black text-xl truncate">Options de personnalisation</h3>
              <p className="text-xs text-[#75807A] truncate">Plat : <b className="text-[#1A1A1A]">{item.name}</b></p>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center shrink-0" data-testid="rt-options-close"><X className="w-5 h-5" /></button>
          </div>

          <div className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/25 p-3 text-xs text-[#7A3A00] mb-4">
            Créez des groupes de choix pour ce plat (ex : <i>Choisissez votre viande</i>, <i>Suppléments</i>).
            Chaque groupe peut être <b>obligatoire</b> et limité en nombre de choix (min/max).
            Chaque choix peut avoir un supplément de prix (0 € accepté).
          </div>

          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F26419]" /></div>
          ) : groups.length === 0 ? (
            <div className="rounded-2xl bg-[#FAF5EC] border-2 border-dashed border-[#E2DFD8] p-8 text-center">
              <Sliders className="w-8 h-8 text-[#F26419] mx-auto opacity-60" />
              <div className="mt-2 text-sm text-[#75807A]">Aucun groupe d'options pour l'instant.</div>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="rt-options-groups">
              {groups.map((g) => (
                <OptionGroupCard
                  key={g.id}
                  group={g}
                  onLocalUpdate={(patch) => updateGroupLocal(g.id, patch)}
                  onDelete={() => removeGroup(g.id)}
                />
              ))}
            </ul>
          )}

          <div className="mt-5">
            <button onClick={addGroup} disabled={creating} data-testid="rt-options-add-group"
              className="w-full h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Ajouter un groupe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OptionGroupCard({ group, onLocalUpdate, onDelete }) {
  const [name, setName] = useState(group.name || "");
  const [required, setRequired] = useState(!!group.required);
  const [minC, setMinC] = useState(group.min_choices ?? 0);
  const [maxC, setMaxC] = useState(group.max_choices ?? 1);
  const [choices, setChoices] = useState(group.choices || []);
  const [defaultChoiceId, setDefaultChoiceId] = useState(group.default_choice_id || null);
  const [addingChoice, setAddingChoice] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (name === group.name && required === !!group.required
          && Number(minC) === Number(group.min_choices)
          && Number(maxC) === Number(group.max_choices)) return;
      try {
        const { data } = await api.patch(`/restaurateur/menu/option-groups/${group.id}`, {
          name, required, min_choices: Number(minC) || 0, max_choices: Number(maxC) || 1,
        });
        setMinC(data.min_choices);
        setMaxC(data.max_choices);
        onLocalUpdate({ name: data.name, required: data.required,
                        min_choices: data.min_choices, max_choices: data.max_choices });
      } catch (e) { toast.error(e?.response?.data?.detail || "Sauvegarde impossible."); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [name, required, minC, maxC]);

  const setDefault = async (choiceId) => {
    setDefaultChoiceId(choiceId);
    try {
      await api.patch(`/restaurateur/menu/option-groups/${group.id}`, { default_choice_id: choiceId || "" });
      onLocalUpdate({ default_choice_id: choiceId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sauvegarde impossible.");
      setDefaultChoiceId(group.default_choice_id || null);
    }
  };

  const addChoice = async () => {
    setAddingChoice(true);
    try {
      const { data } = await api.post(`/restaurateur/menu/option-groups/${group.id}/choices`, {
        name: "Nouveau choix", extra_price_eur: 0, position: choices.length,
      });
      setChoices((s) => [...s, data]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setAddingChoice(false); }
  };

  const updateChoiceLocal = (choiceId, patch) => {
    setChoices((s) => s.map((c) => (c.id === choiceId ? { ...c, ...patch } : c)));
  };

  const removeChoice = async (choiceId) => {
    try {
      await api.delete(`/restaurateur/menu/option-choices/${choiceId}`);
      setChoices((s) => s.filter((c) => c.id !== choiceId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  return (
    <li className="rounded-2xl bg-white border border-[#E2DFD8] p-4" data-testid={`rt-options-group-${group.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <input
          value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
          placeholder="Nom du groupe (ex : Choisissez votre viande)"
          data-testid={`rt-options-group-name-${group.id}`}
          className="flex-1 min-w-[220px] h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#F26419] outline-none font-semibold text-[#1A1A1A]" />
        <button onClick={onDelete} data-testid={`rt-options-group-delete-${group.id}`}
          className="h-11 px-3 rounded-xl bg-white border border-[#D32F2F] text-[#D32F2F] hover:bg-[#FEE2E2] inline-flex items-center justify-center">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-4 flex-wrap text-sm">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)}
            data-testid={`rt-options-group-required-${group.id}`} className="w-4 h-4 accent-[#F26419]" />
          <span className="font-semibold text-[#1A1A1A]">Obligatoire</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <span className="text-[#75807A]">Min</span>
          <input type="number" min="0" max="20" value={minC}
            onChange={(e) => setMinC(e.target.value)}
            data-testid={`rt-options-group-min-${group.id}`}
            className="w-16 h-9 px-2 rounded-lg border border-[#E2DFD8] focus:border-[#F26419] outline-none text-center" />
        </label>
        <label className="inline-flex items-center gap-2">
          <span className="text-[#75807A]">Max</span>
          <input type="number" min="1" max="20" value={maxC}
            onChange={(e) => setMaxC(e.target.value)}
            data-testid={`rt-options-group-max-${group.id}`}
            className="w-16 h-9 px-2 rounded-lg border border-[#E2DFD8] focus:border-[#F26419] outline-none text-center" />
        </label>
      </div>

      <div className="mt-4 border-t border-[#E2DFD8] pt-3">
        <div className="text-xs font-bold text-[#75807A] uppercase tracking-wide mb-2">Choix ({choices.length})</div>
        {choices.length === 0 ? (
          <div className="text-xs text-[#B0AA9E] italic mb-2">Aucun choix. Ajoutez-en un ci-dessous.</div>
        ) : (
          <ul className="space-y-2" data-testid={`rt-options-choices-${group.id}`}>
            {choices.map((c) => (
              <OptionChoiceRow key={c.id} choice={c}
                groupId={group.id}
                defaultChoiceId={defaultChoiceId}
                onSetDefault={setDefault}
                onLocalUpdate={(patch) => updateChoiceLocal(c.id, patch)}
                onDelete={() => removeChoice(c.id)} />
            ))}
          </ul>
        )}
        <button onClick={addChoice} disabled={addingChoice}
          data-testid={`rt-options-add-choice-${group.id}`}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] text-[#1A1A1A] text-xs font-bold disabled:opacity-60">
          {addingChoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Ajouter un choix
        </button>
      </div>
    </li>
  );
}

function OptionChoiceRow({ choice, groupId, defaultChoiceId, onSetDefault, onLocalUpdate, onDelete }) {
  const [name, setName] = useState(choice.name || "");
  const [extra, setExtra] = useState(choice.extra_price_eur ?? 0);
  const isDefault = defaultChoiceId === choice.id;

  useEffect(() => {
    const t = setTimeout(async () => {
      if (name === choice.name && Number(extra) === Number(choice.extra_price_eur)) return;
      try {
        const { data } = await api.patch(`/restaurateur/menu/option-choices/${choice.id}`, {
          name, extra_price_eur: Number(extra) || 0,
        });
        onLocalUpdate({ name: data.name, extra_price_eur: data.extra_price_eur });
      } catch (e) { toast.error(e?.response?.data?.detail || "Sauvegarde impossible."); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [name, extra]);

  return (
    <li className="flex items-center gap-2 flex-wrap" data-testid={`rt-options-choice-${choice.id}`}>
      <button
        type="button"
        onClick={() => onSetDefault(isDefault ? null : choice.id)}
        title={isDefault ? "Choix par défaut — cliquer pour retirer" : "Définir comme choix par défaut"}
        data-testid={`rt-options-choice-default-${choice.id}`}
        className={`w-9 h-9 rounded-lg border inline-flex items-center justify-center transition-colors ${
          isDefault ? "bg-[#FEF3C7] border-[#F59E0B] text-[#B45309]" : "bg-white border-[#E2DFD8] text-[#B0AA9E] hover:text-[#F59E0B]"
        }`}
      >
        <Star className={`w-4 h-4 ${isDefault ? "fill-[#F59E0B]" : ""}`} />
      </button>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
        placeholder="Nom du choix (ex : Bœuf)"
        data-testid={`rt-options-choice-name-${choice.id}`}
        className="flex-1 min-w-[160px] h-9 px-3 rounded-lg border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm" />
      <div className="flex items-center gap-1">
        <span className="text-xs text-[#75807A]">+</span>
        <input type="number" step="0.01" min="0" max="999" value={extra}
          onChange={(e) => setExtra(e.target.value)}
          data-testid={`rt-options-choice-price-${choice.id}`}
          className="w-20 h-9 px-2 rounded-lg border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm text-right" />
        <span className="text-xs text-[#75807A]">€</span>
      </div>
      <button onClick={onDelete} data-testid={`rt-options-choice-delete-${choice.id}`}
        className="w-9 h-9 rounded-lg bg-white border border-[#D32F2F]/60 text-[#D32F2F] hover:bg-[#FEE2E2] inline-flex items-center justify-center">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </li>
  );
}
